import { useEffect } from "react"
import { Modal, Platform, ScrollView, View } from "react-native"
import * as Linking from "expo-linking"
import { Image } from "expo-image"
import Lightning from "phosphor-react-native/src/icons/Lightning"
import ListChecks from "phosphor-react-native/src/icons/ListChecks"
import MapPinArea from "phosphor-react-native/src/icons/MapPinArea"
import MoonStars from "phosphor-react-native/src/icons/MoonStars"
import type { Icon as PhosphorIcon } from "phosphor-react-native/src/index"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { PrimaryDarkButton } from "@/components/buttons/CommonButtons"
import { Body, BodyBold, H1, Subtitle } from "@/components/standard/BFText"
import { getAppStoreListingUrl } from "@/constants/AppStore"
import {
  forceUpgradeStoreLinkClicked,
  forceUpgradeViewed,
} from "@/analytics/events/forceUpgrade"

// Copy mirrors backflip-apps RefreshAnnouncementSheet; keep them in sync.
const FEATURES: ReadonlyArray<{
  title: string
  description: string
  icon: PhosphorIcon
}> = [
  {
    title: "Smarter comp selection",
    description:
      "More comps to choose from, a better map view, and a Comp Match score to find the closest ones",
    icon: MapPinArea,
  },
  {
    title: "Live quotes",
    description:
      "See if Backflip can fund a deal as you analyze it, with a quote that updates as you change your numbers",
    icon: Lightning,
  },
  {
    title: "Loan tracking",
    description:
      "Follow each loan's progress on the go, and see which tasks need you next",
    icon: ListChecks,
  },
  {
    title: "Dark mode",
    description:
      "Easier on the eyes, day or night. Switch anytime in Settings.",
    icon: MoonStars,
  },
]

const SHEET_BG = "#0d1522"
const FG = "#f5f6fa"
const FG_SUBTLE = "#c3ccda"

export interface ForceUpgradeModalProps {
  visible: boolean
  title: string
  message: string
  buttonTitle: string
}

/**
 * Full-screen, undismissable upgrade gate. There is deliberately no close
 * affordance and `onRequestClose` is a no-op so the Android back button and
 * gesture cannot get behind it.
 */
export const ForceUpgradeModal = ({
  visible,
  title,
  message,
  buttonTitle,
}: ForceUpgradeModalProps) => {
  const insets = useSafeAreaInsets()

  useEffect(() => {
    if (visible) {
      forceUpgradeViewed()
    }
  }, [visible])

  const handlePress = () => {
    const url = getAppStoreListingUrl()
    forceUpgradeStoreLinkClicked({ linkText: buttonTitle, linkUrl: url })
    void Linking.openURL(url)
  }

  return (
    <Modal
      testID="force-upgrade-modal"
      visible={visible}
      animationType="fade"
      transparent={false}
      statusBarTranslucent
      presentationStyle={Platform.OS === "ios" ? "fullScreen" : undefined}
      onRequestClose={() => {
        // Intentionally empty: the gate cannot be dismissed.
      }}
    >
      <View className="flex-1" style={{ backgroundColor: SHEET_BG }}>
        <ScrollView
          contentContainerStyle={{
            paddingTop: insets.top + 40,
            paddingHorizontal: 24,
            paddingBottom: 24,
          }}
          showsVerticalScrollIndicator={false}
        >
          <Image
            accessible={false}
            contentFit="contain"
            source={require("@/assets/images/force-upgrade-roller.webp")}
            style={{ alignSelf: "center", width: 148, height: 148 }}
          />
          <H1
            accessibilityRole="header"
            className="mt-5 text-center text-[32px] leading-[36px]"
            style={{ color: FG }}
          >
            {title}
          </H1>
          <Subtitle
            className="mt-2 text-center text-[18px] leading-[24px]"
            style={{ color: FG }}
          >
            {message}
          </Subtitle>

          <View className="mt-8 gap-4">
            {FEATURES.map(
              ({ title: featureTitle, description, icon: Icon }) => (
                <View key={featureTitle} className="flex-row items-start gap-3">
                  <Icon color={FG} size={22} weight="bold" />
                  <View className="flex-1 gap-0.5">
                    <BodyBold
                      className="text-[16px] leading-[22px]"
                      style={{ color: FG }}
                    >
                      {featureTitle}
                    </BodyBold>
                    <Body
                      className="text-[16px] leading-[22px]"
                      style={{ color: FG_SUBTLE }}
                    >
                      {description}
                    </Body>
                  </View>
                </View>
              )
            )}
          </View>
        </ScrollView>

        <View
          className="px-5 pt-3"
          style={{ paddingBottom: insets.bottom + 12 }}
        >
          <PrimaryDarkButton
            testID="force-upgrade-store-button"
            title={buttonTitle}
            onPress={handlePress}
            className="rounded-2xl p-5"
          />
        </View>
      </View>
    </Modal>
  )
}
